import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Store, Wallet, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtDate, fmtGNF, VENDOR_STATUS_LABELS, VENDOR_STATUS_STYLES } from "@/lib/format";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";

export function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [payout, setPayout] = useState(null);
  const [decision, setDecision] = useState(null);
  const [plans, setPlans] = useState([]);

  const load = async () => {
    try {
      const [v, p] = await Promise.all([api.get("/admin/vendors"), api.get("/admin/vendor-products", { params: { pending: true } })]);
      setVendors(v.data);
      setPendingProducts(p.data);
      setPlans((await api.get("/vendor/plans")).data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setPlan = async (v, plan) => {
    try {
      await api.put(`/admin/vendors/${v.id}/plan`, { plan });
      toast.success("Forfait du vendeur mis à jour");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setStatus = async (v, status, reason) => {
    try {
      await api.put(`/admin/vendors/${v.id}/status`, { status, reason: reason || null });
      toast.success(`Vendeur ${VENDOR_STATUS_LABELS[status].toLowerCase()}`);
      setDecision(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const approveProduct = async (p, approved) => {
    try {
      await api.put(`/admin/vendor-products/${p.id}/approval`, null, { params: { approved } });
      toast.success(approved ? "Produit validé et publié" : "Produit refusé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendPayout = async () => {
    try {
      await api.post(`/admin/vendors/${payout.vendor.id}/payouts`, {
        amount: Number(payout.amount),
        method: payout.method,
        reference: payout.reference,
      });
      toast.success("Paiement vendeur enregistré");
      setPayout(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-vendors-page">
      <Panel title="Vendeurs partenaires" subtitle={`${vendors.length} vendeur(s)`} testid="admin-vendors-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">Boutique</th>
                <th className="py-2">Contact</th>
                <th className="py-2">Statut</th>
                <th className="py-2 text-right">Produits</th>
                <th className="py-2 text-right">Ventes</th>
                <th className="py-2 text-right">Commissions</th>
                <th className="py-2 text-right">Solde dû</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 && (
                <tr><td colSpan={8} className="py-4 text-sm text-slate-500">Aucune demande vendeur pour le moment.</td></tr>
              )}
              {vendors.map((v) => (
                <tr key={v.id} className="border-b border-slate-800/60" data-testid={`admin-vendor-row-${v.email}`}>
                  <td className="py-2">
                    <p className="font-medium text-white">{v.shop_name}</p>
                    <p className="text-[11px] text-slate-400">{v.address}</p>
                    <p className="text-[10px] text-slate-500">
                      {v.business_type === "societe" ? "Société" : "Particulier"}
                      {v.tax_id ? ` · NIF ${v.tax_id}` : ""}
                      {v.trade_register ? ` · RCCM ${v.trade_register}` : ""}
                    </p>
                    {(v.categories || []).length > 0 && (
                      <p className="text-[10px] text-slate-500">Catégories : {v.categories.join(", ")}</p>
                    )}
                    <div className="mt-1 flex gap-2">
                      {v.id_document && (
                        <a href={mediaUrl(v.id_document)} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-gold hover:underline" data-testid={`vendor-doc-id-${v.email}`}>
                          Pièce d'identité
                        </a>
                      )}
                      {v.shop_photo && (
                        <a href={mediaUrl(v.shop_photo)} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-gold hover:underline" data-testid={`vendor-doc-shop-${v.email}`}>
                          Photo boutique
                        </a>
                      )}
                    </div>
                    {v.status_reason && <p className="text-[10px] text-rose-300">Motif : {v.status_reason}</p>}
                  </td>
                  <td className="py-2 text-[11px] text-slate-300">
                    {v.email}
                    <br />
                    <span className="font-num">{v.phone}</span>
                  </td>
                  <td className="py-2">
                    <span className={`ds-chip ${VENDOR_STATUS_STYLES[v.status]}`}>{VENDOR_STATUS_LABELS[v.status]}</span>
                    <select
                      value={v.plan || "gratuit"}
                      onChange={(e) => setPlan(v, e.target.value)}
                      className="mt-1 block rounded-lg border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-brand"
                      data-testid={`vendor-plan-${v.email}`}
                    >
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500">
                      {plans.find((p) => p.id === (v.plan || "gratuit"))?.auto_publish
                        ? "publication immédiate"
                        : "validation requise"}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-500">{fmtDate(v.created_at)}</p>
                  </td>
                  <td className="font-num py-2 text-right text-slate-300">{v.products_count}</td>
                  <td className="font-num py-2 text-right text-slate-200">{fmtGNF(v.total_sales)}</td>
                  <td className="font-num py-2 text-right text-gold">{fmtGNF(v.total_commission)}</td>
                  <td className="font-num py-2 text-right font-semibold text-emerald-400">{fmtGNF(v.available_amount)}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap justify-end gap-1">
                      {v.status !== "approved" && (
                        <ABtn onClick={() => setStatus(v, "approved")} data-testid={`admin-vendor-approve-${v.email}`}>Valider</ABtn>
                      )}
                      {v.status === "approved" && (
                        <ABtn variant="danger" onClick={() => setDecision({ vendor: v, status: "suspended", reason: "" })} data-testid={`admin-vendor-suspend-${v.email}`}>Suspendre</ABtn>
                      )}
                      {v.status === "pending" && (
                        <ABtn variant="ghost" onClick={() => setDecision({ vendor: v, status: "rejected", reason: "" })} data-testid={`admin-vendor-reject-${v.email}`}>Refuser</ABtn>
                      )}
                      <ABtn
                        variant="gold"
                        onClick={() => setPayout({ vendor: v, amount: Math.round(v.available_amount), method: "orange_money", reference: "" })}
                        data-testid={`admin-vendor-payout-${v.email}`}
                      >
                        <Wallet className="h-3.5 w-3.5" /> Payer
                      </ABtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Produits vendeurs à valider" subtitle={`${pendingProducts.length} en attente`} testid="admin-vendor-products-panel">        <div className="space-y-2">
          {pendingProducts.length === 0 && <p className="text-sm text-slate-500">Aucun produit en attente de validation.</p>}
          {pendingProducts.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-900/60 bg-amber-950/20 p-3" data-testid={`admin-pending-product-${p.sku}`}>
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-800">
                {(p.images || [])[0] && <img src={mediaUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{p.name}</p>
                <p className="font-num text-[11px] text-slate-400">
                  {p.sku} · {p.owner_name} · {p.category_name} · {fmtGNF(p.price)} · stock {p.stock}
                </p>
              </div>
              <ABtn onClick={() => approveProduct(p, true)} data-testid={`admin-approve-product-${p.sku}`}>
                <Check className="h-3.5 w-3.5" /> Valider
              </ABtn>
              <ABtn variant="danger" onClick={() => approveProduct(p, false)} data-testid={`admin-reject-product-${p.sku}`}>
                <X className="h-3.5 w-3.5" /> Refuser
              </ABtn>
            </div>
          ))}
        </div>
      </Panel>

      {payout && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="admin-payout-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">Payer {payout.vendor.shop_name}</h3>
              <ABtn variant="ghost" onClick={() => setPayout(null)}><X className="h-4 w-4" /></ABtn>
            </div>
            <p className="font-num mt-2 text-xs text-slate-400">Solde disponible : {fmtGNF(payout.vendor.available_amount)}</p>
            <div className="mt-4 space-y-3">
              <div>
                <ALabel>Montant (GNF) *</ALabel>
                <AInput type="number" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} data-testid="payout-amount-input" />
              </div>
              <div>
                <ALabel>Méthode</ALabel>
                <ASelect value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })} data-testid="payout-method-select">
                  <option value="orange_money">Orange Money</option>
                  <option value="mtn_momo">MTN Mobile Money</option>
                  <option value="especes">Espèces</option>
                  <option value="virement">Virement bancaire</option>
                </ASelect>
              </div>
              <div>
                <ALabel>Référence</ALabel>
                <AInput value={payout.reference} onChange={(e) => setPayout({ ...payout, reference: e.target.value })} data-testid="payout-reference-input" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setPayout(null)}>Annuler</ABtn>
              <ABtn variant="gold" onClick={sendPayout} data-testid="payout-submit">Enregistrer le paiement</ABtn>
            </div>
          </div>
        </div>
      )}
      {decision && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="admin-vendor-decision-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">
                {decision.status === "rejected" ? "Refuser" : "Suspendre"} {decision.vendor.shop_name}
              </h3>
              <ABtn variant="ghost" onClick={() => setDecision(null)}><X className="h-4 w-4" /></ABtn>
            </div>
            <div className="mt-4">
              <ALabel>Motif communiqué au vendeur *</ALabel>
              <textarea
                rows={3}
                value={decision.reason}
                onChange={(e) => setDecision({ ...decision, reason: e.target.value })}
                className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-brand"
                placeholder="Dossier incomplet, NIF manquant, produits non conformes…"
                data-testid="vendor-decision-reason"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setDecision(null)}>Annuler</ABtn>
              <ABtn
                variant="danger"
                disabled={!decision.reason.trim()}
                onClick={() => setStatus(decision.vendor, decision.status, decision.reason.trim())}
                data-testid="vendor-decision-submit"
              >
                Confirmer
              </ABtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminCommissions() {
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const [c, cats, rows] = await Promise.all([
        api.get("/admin/commission-config"),
        api.get("/categories", { params: { all: true } }),
        api.get("/admin/commissions"),
      ]);
      setConfig(c.data);
      setCategories(cats.data);
      setData(rows.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/commission-config", {
        global_rate: Number(config.global_rate) || 0,
        fixed_fee: Number(config.fixed_fee) || 0,
        category_rates: config.category_rates,
      });
      toast.success("Configuration des commissions enregistrée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!config || !data) return <p className="text-sm text-slate-400">Chargement…</p>;

  const example = 100000;
  const exampleCommission = example * (Number(config.global_rate) || 0) / 100 + (Number(config.fixed_fee) || 0);

  return (
    <div className="space-y-4" data-testid="admin-commissions-page">
      <Panel
        title="Configuration des commissions"
        subtitle="Taux global, commission fixe et taux par catégorie"
        actions={<ABtn onClick={save} data-testid="commission-save">Enregistrer</ABtn>}
        testid="commission-config-panel"
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <ALabel>Taux global (%)</ALabel>
            <AInput type="number" step="0.5" value={config.global_rate} onChange={(e) => setConfig({ ...config, global_rate: e.target.value })} data-testid="commission-global-rate" />
          </div>
          <div>
            <ALabel>Commission fixe par ligne (GNF)</ALabel>
            <AInput type="number" value={config.fixed_fee} onChange={(e) => setConfig({ ...config, fixed_fee: e.target.value })} data-testid="commission-fixed-fee" />
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-400">Exemple</p>
            <p className="font-num mt-1 text-xs text-slate-300">Vente {fmtGNF(example)}</p>
            <p className="font-num text-xs text-gold">EMPIRE-SHOP {fmtGNF(exampleCommission)}</p>
            <p className="font-num text-xs text-emerald-400">Vendeur {fmtGNF(example - exampleCommission)}</p>
          </div>
        </div>

        <p className="mt-5 text-[11px] uppercase tracking-wider text-slate-400">Taux par catégorie (vide = taux global)</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-800 bg-slate-950 p-2.5">
              <p className="truncate text-xs font-medium text-slate-300">{c.name}</p>
              <div className="mt-1.5 flex items-center gap-2">
                <AInput
                  type="number"
                  step="0.5"
                  placeholder={`${config.global_rate}`}
                  value={config.category_rates?.[c.id] ?? ""}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      category_rates: { ...config.category_rates, [c.id]: e.target.value },
                    })
                  }
                  className="py-1.5"
                  data-testid={`commission-rate-${c.slug}`}
                />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ["Ventes vendeurs", data.totals.ventes_vendeurs],
          ["Commissions totales", data.totals.commissions],
          ["Commissions encaissées", data.totals.commissions_encaissees],
          ["Dû aux vendeurs (livré)", data.totals.du_aux_vendeurs],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-slate-800 bg-slate-900 p-4" data-testid={`commission-total-${label}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
            <p className="font-num mt-2 text-lg font-bold text-white">{fmtGNF(value)}</p>
          </div>
        ))}
      </div>

      <Panel title="Détail des commissions" subtitle={`${data.rows.length} ligne(s) de vente vendeur`} testid="commission-rows-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">Commande</th>
                <th className="py-2">Vendeur</th>
                <th className="py-2">Produit</th>
                <th className="py-2">Statut</th>
                <th className="py-2 text-right">Vente</th>
                <th className="py-2 text-right">Taux</th>
                <th className="py-2 text-right">Commission</th>
                <th className="py-2 text-right">Part vendeur</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={8} className="py-4 text-sm text-slate-500">Aucune vente vendeur enregistrée.</td></tr>
              )}
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-800/60">
                  <td className="font-num py-2 text-slate-300">{r.order_number}</td>
                  <td className="py-2 text-white"><Store className="mr-1 inline h-3 w-3 text-gold" />{r.vendor_name}</td>
                  <td className="py-2 text-slate-200">{r.product_name}</td>
                  <td className="py-2 text-slate-400">{r.status}</td>
                  <td className="font-num py-2 text-right text-slate-200">{fmtGNF(r.total)}</td>
                  <td className="font-num py-2 text-right text-slate-400">{r.commission_rate} %</td>
                  <td className="font-num py-2 text-right text-gold">{fmtGNF(r.commission_amount)}</td>
                  <td className="font-num py-2 text-right text-emerald-400">{fmtGNF(r.vendor_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
